import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    IconButton,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { AdminPageContainer } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { getHolidays, addHoliday, editHoliday, deleteHoliday } from '../../redux/ducks/holidays';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../redux/slices/alerts';
import AppDatePicker from '../../components/AppDatePicker';
import { formatDate, formatDayLong } from '../../utils/dateUtils';

const FilterRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
}));

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const DialogStack = styled(Stack)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const YEAR_SPAN = 5;

const AdminHolidays = () => {
    const dispatch = useDispatch();
    const currentYear = new Date().getFullYear();

    const [year, setYear] = useState(currentYear);
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ date: '', name: '', description: '' });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [toDelete, setToDelete] = useState(null);

    const loadHolidays = (yr) => {
        setLoading(true);
        dispatch(getHolidays({ year: yr }, (data) => {
            setHolidays(data?.holidays || []);
            setLoading(false);
        }));
    };

    useEffect(() => {
        loadHolidays(year);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year]);

    const openCreate = () => {
        setDialogMode('create');
        setSelected(null);
        setForm({ date: '', name: '', description: '' });
        setDialogOpen(true);
    };

    const openEdit = (holiday) => {
        setDialogMode('edit');
        setSelected(holiday);
        setForm({
            date: formatDate(holiday.date),
            name: holiday.name || '',
            description: holiday.description || '',
        });
        setDialogOpen(true);
    };

    const handleSave = () => {
        if (!form.date || !form.name) {
            dispatch(showAlert({ severity: 'warning', message: 'Date and name are required' }));
            return;
        }
        const cb = (response) => {
            const failed = !response || response?.error;
            if (failed) {
                dispatch(showAlert({ severity: 'error', message: response?.error || 'Could not save holiday' }));
                return;
            }
            dispatch(showAlert({ severity: 'success', message: dialogMode === 'create' ? 'Holiday created' : 'Holiday updated' }));
            setDialogOpen(false);
            loadHolidays(year);
        };
        if (dialogMode === 'create') {
            dispatch(addHoliday(form.date, form.name, form.description, cb));
        } else {
            dispatch(editHoliday(selected._id, form.date, form.name, form.description, cb));
        }
    };

    const handleConfirmDelete = () => {
        dispatch(deleteHoliday(toDelete._id, (response) => {
            const failed = !response || response?.error;
            if (failed) {
                dispatch(showAlert({ severity: 'error', message: response?.error || 'Could not delete holiday' }));
            } else {
                dispatch(showAlert({ severity: 'success', message: 'Holiday deleted' }));
                loadHolidays(year);
            }
            setConfirmOpen(false);
            setToDelete(null);
        }));
    };

    const yearOptions = [];
    for (let y = currentYear - YEAR_SPAN; y <= currentYear + YEAR_SPAN; y += 1) {
        yearOptions.push(y);
    }

    return (
        <AdminPageContainer>
            <PageHeader
                title="Holidays Management"
                subtitle="Manage organization-wide holidays. Holidays are excluded from time-off working-day totals."
                actions={
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
                        Add Holiday
                    </Button>
                }
            />

            <FilterRow>
                <TextField
                    select
                    label="Year"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value, 10))}
                >
                    {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                </TextField>
            </FilterRow>

            {loading ? (
                <LoadingRow><CircularProgress /></LoadingRow>
            ) : holidays.length === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        No holidays configured for {year}. Click "Add Holiday" to create one.
                    </Typography>
                </EmptyState>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {holidays.map((h) => (
                                <TableRow key={h._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{formatDayLong(h.date)}</Typography>
                                    </TableCell>
                                    <TableCell>{h.name}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">{h.description || '—'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => openEdit(h)} color="primary">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => { setToDelete(h); setConfirmOpen(true); }} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{dialogMode === 'create' ? 'Add Holiday' : 'Edit Holiday'}</DialogTitle>
                <DialogContent>
                    <DialogStack spacing={2}>
                        <AppDatePicker
                            label="Date"
                            value={form.date}
                            onChange={(v) => setForm({ ...form, date: v })}
                        />
                        <TextField
                            label="Name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                    </DialogStack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {dialogMode === 'create' ? 'Create' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmOpen}
                onClose={() => { setConfirmOpen(false); setToDelete(null); }}
                onConfirm={handleConfirmDelete}
                message={`Are you sure you want to delete "${toDelete?.name}"?`}
            />
        </AdminPageContainer>
    );
};

export default AdminHolidays;
